/**
 * AES-256-CBC encryption matching Unity's Encryptor.cs
 * Key: "9b9b9b9b9b9b9b9b9b9b9b9b9b9b9b96" (32 ASCII bytes)
 * IV: "9b9b9b9b9b9b9b9b" (16 ASCII bytes)
 * Padding: PKCS7 (Web Crypto default for AES-CBC)
 */

const ENCRYPTION_KEY_RAW = new TextEncoder().encode("9b9b9b9b9b9b9b9b9b9b9b9b9b9b9b96");
const ENCRYPTION_IV = new TextEncoder().encode("9b9b9b9b9b9b9b9b");

let cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;
  cachedKey = await crypto.subtle.importKey("raw", ENCRYPTION_KEY_RAW, { name: "AES-CBC" }, false, [
    "encrypt",
  ]);
  return cachedKey;
}

const NO_ENCRYPT_TYPES = ["video", "audio", "avatar"];

export function shouldEncrypt(mediaType: string, filename: string): boolean {
  if (NO_ENCRYPT_TYPES.includes(mediaType)) return false;
  return !filename.toLowerCase().endsWith(".gif");
}

const AES_BLOCK_BYTES = 16;
const ENCRYPTION_BATCH_BYTES = 1024 * 1024;

export function createMediaEncryptionStream(): TransformStream<Uint8Array, Uint8Array> {
  let iv: Uint8Array<ArrayBuffer> = ENCRYPTION_IV;
  let pending: Uint8Array[] = [];
  let pendingBytes = 0;

  const takePending = (): Uint8Array<ArrayBuffer> => {
    const merged = new Uint8Array(pendingBytes);
    let offset = 0;
    for (const chunk of pending) {
      merged.set(chunk, offset);
      offset += chunk.byteLength;
    }
    pending = [];
    pendingBytes = 0;
    return merged;
  };

  return new TransformStream<Uint8Array, Uint8Array>({
    async transform(chunk, controller) {
      pending.push(chunk);
      pendingBytes += chunk.byteLength;
      if (pendingBytes < ENCRYPTION_BATCH_BYTES) return;

      const data = takePending();
      const usable = data.byteLength - (data.byteLength % AES_BLOCK_BYTES);
      if (usable < data.byteLength) {
        pending = [data.slice(usable)];
        pendingBytes = data.byteLength - usable;
      }
      const key = await getKey();
      const encrypted = new Uint8Array(
        await crypto.subtle.encrypt({ name: "AES-CBC", iv }, key, data.subarray(0, usable)),
      );
      const blocks = encrypted.slice(0, usable);
      iv = blocks.slice(usable - AES_BLOCK_BYTES);
      controller.enqueue(blocks);
    },
    async flush(controller) {
      const key = await getKey();
      const encrypted = await crypto.subtle.encrypt({ name: "AES-CBC", iv }, key, takePending());
      controller.enqueue(new Uint8Array(encrypted));
    },
  });
}
